import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, User } from './shared/services/auth.service';
import { DataInitService } from './shared/services/data-init.service';
import { TableService } from './shared/services/table.service';
import { environment } from '../environments/environment';

/** Rutas que cada rol puede ver en el menú (el cliente no accede al sistema). */
const ROLE_ROUTES: Record<User['role'], string[]> = {
  admin: ['/dashboard', '/floor-plan', '/menu', '/orders', '/kitchen', '/billing', '/reports', '/insumos', '/configuracion'],
  mesero: ['/dashboard', '/floor-plan', '/menu', '/orders'],
  cocinero: ['/dashboard', '/kitchen'],
  cajero: ['/dashboard', '/billing', '/reports', '/insumos'],
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  isDropdownOpen = false;
  isUserMenuOpen = false;
  isMobileMenuOpen = false;
  scrolled = false;
  private clickListener?: () => void;

  constructor(
    private authService: AuthService,
    private router: Router,
    private dataInitService: DataInitService,
    public tableService: TableService
  ) {}

  /** Indica si el usuario actual puede ver la ruta en el menú. */
  canShowRoute(path: string): boolean {
    const user = this.authService.getCurrentUser();
    if (!user) return false;
    return ROLE_ROUTES[user.role]?.includes(path) ?? false;
  }

  isAdmin(): boolean {
    return this.authService.getCurrentUser()?.role === 'admin';
  }

  onSedeChange(locationId: string): void {
    this.tableService.setSelectedLocationId(locationId || null);
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    this.scrolled = window.scrollY > 20;
  }

  ngOnInit(): void {
    // En modo DEMO la inicialización se hace desde el login al aceptar el mensaje de bienvenida
    if (!environment.demo) {
      this.dataInitService.initializeTestData();
    }

    // Cerrar dropdowns al hacer clic fuera
    this.clickListener = () => {
      if (this.isDropdownOpen) {
        this.closeDropdown();
      }
      if (this.isUserMenuOpen) {
        this.closeUserMenu();
      }
      if (this.isMobileMenuOpen) {
        this.closeMobileMenu();
      }
    };
    document.addEventListener('click', this.clickListener);
  }

  ngOnDestroy(): void {
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
    }
  }

  isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  getCurrentUser() {
    return this.authService.getCurrentUser();
  }

  getRoleLabel(role: string): string {
    const roles: { [key: string]: string } = {
      'admin': 'Administrador',
      'mesero': 'Mesero',
      'cocinero': 'Cocinero',
      'cajero': 'Cajero'
    };
    return roles[role] || role;
  }

  toggleDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.isUserMenuOpen = false;
      this.isMobileMenuOpen = false;
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  toggleUserMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isUserMenuOpen = !this.isUserMenuOpen;
    if (this.isUserMenuOpen) {
      this.isDropdownOpen = false;
      this.isMobileMenuOpen = false;
    }
  }

  closeUserMenu(): void {
    this.isUserMenuOpen = false;
  }

  onDropdownItemClick(): void {
    setTimeout(() => {
      this.closeDropdown();
      this.closeMobileMenu();
    }, 100);
  }

  toggleMobileMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (this.isMobileMenuOpen) {
      this.isDropdownOpen = false;
      this.isUserMenuOpen = false;
    }
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
    this.closeUserMenu();
    this.closeMobileMenu();
  }
}

